
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Utensils } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

const RecipeSuggestionsFloatingButton = () => {
  const { user } = useAuth();
  const [isHousehold, setIsHousehold] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    // Check if user has household persona
    const checkUserPersona = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('persona')
          .eq('id', user.id)
          .single();
          
        if (error) throw error;
        
        // Only show for household users
        setIsHousehold(data?.persona === 'household');
      } catch (error) {
        console.error("Error checking user persona:", error);
      }
    };
    
    // Check if we should show the button (has expiring items)
    const checkExpiringItems = async () => {
      if (!user) return;
      
      try {
        const now = new Date();
        const sevenDaysLater = new Date();
        sevenDaysLater.setDate(now.getDate() + 7);
        
        const { count, error } = await supabase
          .from('inventory_items')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('has_expiry', true)
          .gte('expiry_date', now.toISOString().split('T')[0])
          .lte('expiry_date', sevenDaysLater.toISOString().split('T')[0]);
          
        if (error) throw error;
        
        // Show button if there are expiring items
        setIsVisible(Boolean(count && count > 0));
      } catch (error) {
        console.error("Error checking expiring items:", error);
      }
    };
    
    if (user) {
      checkUserPersona();
      checkExpiringItems();
      
      // Set up interval to periodically check
      const interval = setInterval(checkExpiringItems, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);
  
  if (!isHousehold || !isVisible) {
    return null;
  }
  
  return (
    <AnimatePresence>
      <motion.div
        className="fixed bottom-8 right-8 z-50"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0, opacity: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
      >
        <Link to="/recipes">
          <motion.button
            className="flex items-center gap-2 bg-gradient-to-r from-expitrack-primary to-green-500 text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Utensils className="h-5 w-5" />
            <span className="font-medium">Recipe Suggestions</span>
          </motion.button>
        </Link>
      </motion.div>
    </AnimatePresence>
  );
};

export default RecipeSuggestionsFloatingButton;
